import * as aggregates from "../common/aggregates";
import PeerReview from "./review/PeerReview";
import ErrorDialog from "components/error/ErrorDialog";
import Dashboard from "components/layout/Dashboard";
import DashboardContent from "components/layout/DashboardContent";
import DashboardHeader from "components/layout/DashboardHeader";
import EmbeddedDashboardReport from "components/reportsV3/EmbeddedDashboardReport";
import DashboardReport from "components/review/DashboardReport";
import { SUBMISSION_STATUS_USE_V3_REPORT } from "flags";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import * as staticDataService from "services/staticDataService";
import { useConfigQuery } from "services/staticDataService";
import { useIsVisible } from "store/hooks/staticDataHooks";
import * as userSelectors from "store/selectors/user/userSelectors";

const DEFAULT_REPORT_SUMMARY_KEY = "DEFAULT";

const defaultDashboardSummaryConfig = [
  {
    method: aggregates.METHOD.MAX,
    key: "limit",
    label: "Largest Single Limit",
    prefix: "$",
  },
  {
    method: aggregates.METHOD.SUM,
    key: "limit",
    label: "Total Limit",
    prefix: "$",
  },
  {
    method: aggregates.METHOD.SUM,
    key: "premium",
    label: "Total GWP",
    prefix: "$",
  },
  {
    method: aggregates.METHOD.SUM,
    key: "brokeragePremium",
    label: "Total Brokerage",
    prefix: "$",
  },
];

const getSummaryConfig = (summaryConfig) => {
  if (summaryConfig === DEFAULT_REPORT_SUMMARY_KEY) {
    return defaultDashboardSummaryConfig;
  }
  return summaryConfig;
};

const makeDashboardTab = (config) => {
  return {
    title: config.title,
    path: config.path,
    hidden: config.hidden,
    element: (
      <DashboardReport
        config={{
          ...config,
          summaryConfig: getSummaryConfig(config?.summaryConfig),
        }}
      />
    ),
  };
};

const ReviewApp = () => {
  const userConfig = useSelector(userSelectors.selectUserConfig);
  const {
    data: dashboardConfig,
    isLoading: dashboardConfigLoading,
  } = staticDataService.useConfigQuery("dashboards");

  const {
    data: submissionReviewConfig,
    isLoading: submissionReviewConfigLoading,
  } = useConfigQuery("submission_review_config");

  const { isVisible: submissionReviewSummaryVisible } = useIsVisible(
    "review.metrics"
  );

  const { isVisible: useV3SubmissionStatus } = useIsVisible(
    SUBMISSION_STATUS_USE_V3_REPORT
  );

  const dashboards = dashboardConfig?.active ?? [];
  const locationSegments = useLocation().pathname.split("/");

  const tabs = [
    {
      title: "Submission Status",
      path: "submissions",
      element: submissionReviewConfigLoading ? (
        <></>
      ) : useV3SubmissionStatus ? (
        <EmbeddedDashboardReport />
      ) : (
        <DashboardReport
          config={{
            summaryConfig:
              submissionReviewSummaryVisible ?? true
                ? getSummaryConfig(
                    submissionReviewConfig?.reviewMetrics ??
                      DEFAULT_REPORT_SUMMARY_KEY
                  )
                : undefined,
            columnConfig: submissionReviewConfig,
          }}
        />
      ),
    },
    {
      title: "Peer Review",
      path: "peer-review",
      element: <PeerReview />,
    },
    ...dashboards.map(makeDashboardTab),
  ]
    .filter(
      (tab) => userConfig?.componentVisibility?.[`review.${tab?.path}`] ?? true
    )
    .map((tab) => ({
      ...tab,
      hidden: tab.hidden && !locationSegments.includes(tab.path),
    }));
  return (
    <div className={"app"}>
      <ErrorDialog />
      <Dashboard>
        <DashboardHeader
          tabs={tabs.filter((tab) => tab.hidden !== true)}
          hideActiveSubmission
          isFullWidth
          topNavBorderless={true}
        />
        <DashboardContent isFullWidth>
          <Routes>
            {tabs.map(({ element, path }) => (
              <Route key={path} path={`${path}/*`} element={element} />
            ))}
            {!dashboardConfigLoading && (
              <Route
                path={"*"}
                element={<Navigate to={tabs[0].path} replace={true} />}
              />
            )}
          </Routes>
        </DashboardContent>
      </Dashboard>
    </div>
  );
};

export default ReviewApp;
